/**
 * Making a fill sit in the photograph.
 *
 * An inpainter works at 512 pixels and its output is scaled up to wherever the
 * hole was, so two things give it away on a sharp phone photograph: a hard edge
 * where it meets the original, and a patch that is smoother than everything
 * around it because the sensor noise never made it through the model. Both are
 * fixed here, in plain arrays, so they can be tested without a browser.
 */

/** Separable box blur on a single-channel float buffer, in place. */
function blur(buf: Float32Array, w: number, h: number, r: number): void {
  if (r < 1) return;
  const tmp = new Float32Array(w * h);
  const norm = 1 / (2 * r + 1);
  for (let y = 0; y < h; y++) {
    let acc = 0;
    for (let x = -r; x <= r; x++) acc += buf[y * w + Math.min(w - 1, Math.max(0, x))];
    for (let x = 0; x < w; x++) {
      tmp[y * w + x] = acc * norm;
      acc += buf[y * w + Math.min(w - 1, x + r + 1)] - buf[y * w + Math.max(0, x - r)];
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let y = -r; y <= r; y++) acc += tmp[Math.min(h - 1, Math.max(0, y)) * w + x];
    for (let y = 0; y < h; y++) {
      buf[y * w + x] = acc * norm;
      acc += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x];
    }
  }
}

/**
 * Blend weight for the fill: 1 inside the hole, falling to 0 a few pixels
 * outside it. The fill is never weakened inside the hole — anything less than
 * full strength there would let the phone show through.
 */
export function featherAlpha(hole: Uint8Array, w: number, h: number, radius: number): Float32Array {
  const a = new Float32Array(w * h);
  for (let i = 0; i < a.length; i++) a[i] = hole[i] ? 1 : 0;
  blur(a, w, h, Math.max(1, Math.round(radius)));
  for (let i = 0; i < a.length; i++) {
    // Rescale so the ramp sits outside the hole: blurred values at the hole's
    // edge are about 0.5, and those become 1.
    a[i] = hole[i] ? 1 : Math.min(1, a[i] * 2);
  }
  return a;
}

/** Luma high-pass: each pixel minus its 3×3 neighbourhood mean. */
function detail(rgba: Uint8ClampedArray, w: number, h: number): Float32Array {
  const luma = new Float32Array(w * h);
  for (let i = 0; i < luma.length; i++) luma[i] = 0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2];
  const smooth = luma.slice();
  blur(smooth, w, h, 1);
  for (let i = 0; i < luma.length; i++) luma[i] -= smooth[i];
  return luma;
}

/**
 * Noise level as a robust sigma: 1.4826 × the median absolute high-pass.
 *
 * Not RMS. Around a phone there is always texture — a jacket's weave, a
 * shirt's stripes, the edge of a hand — and RMS counts all of it as noise.
 * Measured that way on a dark jacket it asked for grain heavy enough to look
 * like spray paint. The median ignores edges and texture and keeps the sensor.
 */
function sigma(values: Float32Array, where: (i: number) => boolean): number {
  const picked: number[] = [];
  for (let i = 0; i < values.length; i++) if (where(i)) picked.push(Math.abs(values[i]));
  if (picked.length === 0) return 0;
  picked.sort((a, b) => a - b);
  return 1.4826 * picked[picked.length >> 1];
}

/** Never add more than this much grain, in luma levels, whatever was measured. */
const GRAIN_CAP = 4;

/** A small seeded generator, so the same photograph always gets the same grain. */
function gaussian(seed: number): () => number {
  let s = seed >>> 0 || 1;
  const uniform = () => ((s = (s * 1664525 + 1013904223) >>> 0) + 1) / 4294967297;
  return () => Math.sqrt(-2 * Math.log(uniform())) * Math.cos(2 * Math.PI * uniform());
}

export interface FinishReport {
  /** Sensor noise measured just around the hole, in luma levels. */
  aroundNoise: number;
  /** Noise the fill arrived with. */
  fillNoise: number;
  /** Noise added to the fill to match. */
  added: number;
}

/**
 * Lay `fill` into `original` over `hole`, feathered at the edge and with grain
 * matched to the photograph around it.
 *
 * All three arrays describe the same w×h region. `original` is updated in place.
 */
export function composite(
  original: Uint8ClampedArray,
  fill: Uint8ClampedArray,
  hole: Uint8Array,
  w: number,
  h: number,
  featherPx = Math.max(2, Math.round(Math.min(w, h) / 200)),
): FinishReport {
  const n = w * h;
  // Grain is measured in a ring just outside the hole: near enough to share its
  // lighting and focus, far enough not to be the hole's own edge.
  const ring = new Float32Array(n);
  for (let i = 0; i < n; i++) ring[i] = hole[i] ? 1 : 0;
  blur(ring, w, h, Math.max(3, featherPx * 4));
  const around = detail(original, w, h);
  const filled = detail(fill, w, h);
  const aroundNoise = sigma(around, (i) => !hole[i] && ring[i] > 0.02 && ring[i] < 0.5);
  const fillNoise = sigma(filled, (i) => hole[i] === 1);
  const added =
    aroundNoise > fillNoise ? Math.min(GRAIN_CAP, Math.sqrt(aroundNoise * aroundNoise - fillNoise * fillNoise)) : 0;

  const alpha = featherAlpha(hole, w, h, featherPx);
  const rand = gaussian(n ^ (w << 8));
  for (let i = 0; i < n; i++) {
    const a = alpha[i];
    if (a <= 0) continue;
    // Monochrome grain: sensor noise after a phone's processing is mostly luma.
    const g = hole[i] ? added * rand() : 0;
    for (let c = 0; c < 3; c++) {
      const k = i * 4 + c;
      original[k] = original[k] * (1 - a) + (fill[k] + g) * a;
    }
  }
  return { aroundNoise, fillNoise, added };
}
